import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { uploadSmart } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserAvatar } from "@/components/shared";
import { Checkbox } from "@/components/ui/checkbox";
import { isPrivileged, setPrivileged } from "@/lib/privilege";
import { UserCircle2, KeyRound, Eye, EyeOff, BellRing, Camera, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = { superadmin: "Süper Admin", customer: "Müşteri", supplier: "Tedarikçi" };

const PREFS_BY_ROLE = {
  supplier: [
    { key: "new_request", label: "Yeni teklif talebi ve talep güncellemeleri" },
    { key: "reminders", label: "Hatırlatma e-postaları" },
    { key: "offer_result", label: "Teklif sonucu bildirimleri (kabul / red / geri alma)" },
  ],
  customer: [
    { key: "new_offer", label: "Yeni teklif geldiğinde" },
    { key: "decision", label: "Proje onay ve karar bildirimleri" },
  ],
  superadmin: [
    { key: "admin_notifications", label: "Sistem bildirimleri (başvuru, yeni proje, müşteri tercihi, SSS önerisi)" },
    { key: "weekly_summary", label: "Haftalık pazartesi özeti" },
  ],
};

const fmtLogin = (l) => {
  if (!l?.at) return null;
  const d = new Date(l.at);
  return `${d.toLocaleDateString("tr-TR")} ${d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}${l.device ? ` — ${l.device}` : ""}`;
};

const CROP_SIZE = 288;

const AvatarCropDialog = ({ src, onCancel, onSave, busy }) => {
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [base, setBase] = useState(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);

  const clamp = (o, z, b = base) => {
    if (!b) return o;
    const maxX = Math.max(0, (b.w * z - CROP_SIZE) / 2);
    const maxY = Math.max(0, (b.h * z - CROP_SIZE) / 2);
    return { x: Math.min(maxX, Math.max(-maxX, o.x)), y: Math.min(maxY, Math.max(-maxY, o.y)) };
  };

  const onLoad = (e) => {
    const { naturalWidth: nw, naturalHeight: nh } = e.target;
    const b = nw >= nh ? { w: (CROP_SIZE * nw) / nh, h: CROP_SIZE } : { w: CROP_SIZE, h: (CROP_SIZE * nh) / nw };
    setBase(b);
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const d = dragRef.current;
    setOffset(clamp({ x: d.ox + (e.clientX - d.sx), y: d.oy + (e.clientY - d.sy) }, zoom));
  };
  const endDrag = () => { dragRef.current = null; };

  const confirm = () => {
    const img = imgRef.current;
    if (!img || !base) return;
    const displayedW = base.w * zoom;
    const ratio = img.naturalWidth / displayedW;
    const tlx = CROP_SIZE / 2 - (base.w * zoom) / 2 + offset.x;
    const tly = CROP_SIZE / 2 - (base.h * zoom) / 2 + offset.y;
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, -tlx * ratio, -tly * ratio, CROP_SIZE * ratio, CROP_SIZE * ratio, 0, 0, 512, 512);
    canvas.toBlob((blob) => { if (blob) onSave(blob); }, "image/png");
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-sm" data-testid="avatar-crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight">Fotoğrafı Hizala</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-slate-500">Fotoğrafı sürükleyerek konumlandırın, kaydırıcıyla yakınlaştırın.</p>
          <div
            className="relative mx-auto overflow-hidden bg-slate-100 touch-none cursor-grab active:cursor-grabbing select-none"
            style={{ width: CROP_SIZE, height: CROP_SIZE }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerLeave={endDrag}
            data-testid="avatar-crop-area"
          >
            <img
              ref={imgRef}
              src={src}
              alt=""
              draggable={false}
              onLoad={onLoad}
              className="absolute left-1/2 top-1/2 max-w-none pointer-events-none"
              style={base ? { width: base.w, height: base.h, transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${zoom})` } : { opacity: 0 }}
            />
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white" style={{ width: CROP_SIZE - 16, height: CROP_SIZE - 16, boxShadow: "0 0 0 9999px rgba(15,23,42,0.45)" }} />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">Yakınlaştır</span>
            <input
              type="range"
              min="1"
              max="3"
              step="0.01"
              value={zoom}
              onChange={(e) => { const z = parseFloat(e.target.value); setZoom(z); setOffset((o) => clamp(o, z)); }}
              className="flex-1 accent-slate-900"
              data-testid="avatar-zoom-slider"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={confirm} disabled={busy || !base} data-testid="avatar-crop-save" className="flex-1 rounded-none bg-slate-900 text-white hover:bg-slate-700 h-10 font-semibold transition-colors duration-200">
              {busy ? "Yükleniyor..." : "Kaydet"}
            </Button>
            <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="avatar-crop-cancel" className="rounded-none h-10 border-slate-200 hover:bg-slate-100 transition-colors duration-200">
              Vazgeç
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PasswordInput = ({ value, onChange, testid, placeholder }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input type={show ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder} data-testid={testid} className="rounded-none border-slate-200 h-11 pr-10" />
      <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors duration-150" tabIndex={-1}>
        {show ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
};

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = ["sifre", "bildirimler"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "profil";
  const isAdmin = user?.role === "superadmin";

  const [form, setForm] = useState({
    company_name: user?.company_name || "",
    contact_name: user?.contact_name || "",
    contact_phone: user?.contact_phone || "",
    website: user?.website || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [spw, setSpw] = useState({ current: "", next: "", confirm: "" });
  const [spwBusy, setSpwBusy] = useState(false);
  const [spwSet, setSpwSet] = useState(null);
  const [privileged, setPrivilegedState] = useState(isPrivileged());
  const [askPw, setAskPw] = useState(false);
  const [vpw, setVpw] = useState("");
  const [vBusy, setVBusy] = useState(false);
  const fullAdmin = user?.role === "superadmin" && (user?.permissions || ["all"]).includes("all");

  const togglePrivilege = (checked) => {
    if (!checked) {
      setPrivileged(false);
      setPrivilegedState(false);
      setAskPw(false);
      toast.success("Özel yetki kapatıldı");
    } else {
      setAskPw(true);
      setVpw("");
    }
  };

  const confirmPrivilege = async () => {
    if (!vpw) return;
    setVBusy(true);
    try {
      const { data } = await api.post("/super-password/verify", { super_password: vpw });
      setPrivileged(true, data.privilege_token);
      setPrivilegedState(true);
      setAskPw(false);
      setVpw("");
      toast.success("Özel yetki etkinleştirildi — teklif sayfalarında düzenleme ikonu görünür");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setVBusy(false);
    }
  };

  useEffect(() => {
    if (fullAdmin) api.get("/super-password/status").then((r) => setSpwSet(r.data.set)).catch(() => {});
  }, [fullAdmin]);

  const changeSuperPassword = async () => {
    if (spw.next.length < 6) { toast.error("Süper şifre en az 6 karakter olmalıdır"); return; }
    if (spw.next !== spw.confirm) { toast.error("Yeni süper şifreler eşleşmiyor"); return; }
    setSpwBusy(true);
    try {
      await api.post("/super-password", { new_super_password: spw.next });
      toast.success(spwSet ? "Süper şifre değiştirildi" : "Süper şifre tanımlandı");
      setSpw({ current: "", next: "", confirm: "" });
      setSpwSet(true);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSpwBusy(false);
    }
  };

  const fileRef = useRef(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [prefs, setPrefs] = useState(user?.email_prefs || {});

  const onAvatarFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Lütfen bir görsel dosyası seçin"); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Fotoğraf en fazla 10MB olabilir"); return; }
    setCropSrc(URL.createObjectURL(f));
  };

  const saveCropped = async (blob) => {
    setAvatarBusy(true);
    try {
      const file = new File([blob], "avatar.png", { type: "image/png" });
      const [uploaded] = await uploadSmart([file]);
      const { data } = await api.post("/me/avatar", { file_id: uploaded.id });
      updateUser({ avatar_file_id: data.avatar_file_id });
      toast.success("Profil fotoğrafınız güncellendi");
      URL.revokeObjectURL(cropSrc);
      setCropSrc(null);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setAvatarBusy(false);
    }
  };

  const removeAvatar = async () => {
    try {
      await api.delete("/me/avatar");
      updateUser({ avatar_file_id: null });
      toast.success("Profil fotoğrafı kaldırıldı");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const togglePref = async (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    try {
      const { data } = await api.put("/me/email-prefs", { prefs: { [key]: value } });
      updateUser({ email_prefs: data.email_prefs });
      toast.success(value ? "Bildirim açıldı" : "Bildirim kapatıldı");
    } catch (err) {
      setPrefs(prefs);
      toast.error(apiError(err));
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/profile", form);
      updateUser(data);
      toast.success("Profiliniz güncellendi");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (pw.next.length < 6) { toast.error("Yeni şifre en az 6 karakter olmalıdır"); return; }
    if (pw.next !== pw.confirm) { toast.error("Yeni şifreler eşleşmiyor"); return; }
    setPwBusy(true);
    try {
      const { data } = await api.post("/me/change-password", { current_password: pw.current, new_password: pw.next });
      if (data?.token) localStorage.setItem("cop31_token", data.token);
      toast.success("Şifreniz başarıyla değiştirildi — diğer cihazlardaki oturumlar sonlandırıldı");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="profile-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-1">Hesap</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter">Profilim</h1>
      </div>

      <Tabs value={tab} onValueChange={(v) => setSearchParams(v === "profil" ? {} : { tab: v })}>
        <TabsList className="rounded-none bg-white border border-slate-200 h-11 p-1 mb-6">
          <TabsTrigger value="profil" data-testid="tab-profile" className="rounded-none h-full px-4 gap-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white font-semibold text-xs">
            <UserCircle2 size={14} /> Profilim
          </TabsTrigger>
          <TabsTrigger value="sifre" data-testid="tab-password" className="rounded-none h-full px-4 gap-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white font-semibold text-xs">
            <KeyRound size={14} /> Şifre İşlemleri
          </TabsTrigger>
          <TabsTrigger value="bildirimler" data-testid="tab-notifications" className="rounded-none h-full px-4 gap-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white font-semibold text-xs">
            <BellRing size={14} /> Bildirimler
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profil">
          <div className="border border-slate-200 bg-white p-6 space-y-5">
            <div className="flex items-center gap-4 pb-5 border-b border-slate-100">
              <UserAvatar userId={user?.id} avatarId={user?.avatar_file_id} name={user?.contact_name || user?.company_name || user?.username} size={72} />
              <div className="space-y-2">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onAvatarFile} data-testid="avatar-file-input" />
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={avatarBusy} data-testid="upload-avatar-button" className="rounded-none h-9 border-slate-200 hover:bg-slate-100 gap-2 font-semibold text-xs transition-colors duration-200">
                    <Camera size={13} /> {avatarBusy ? "Yükleniyor..." : "Fotoğraf Yükle"}
                  </Button>
                  {user?.avatar_file_id && (
                    <Button variant="outline" size="sm" onClick={removeAvatar} data-testid="remove-avatar-button" className="rounded-none h-9 border-red-500/30 text-red-500 hover:bg-red-500/10 gap-2 text-xs transition-colors duration-200">
                      <Trash2 size={13} /> Kaldır
                    </Button>
                  )}
                </div>
                <p className="text-[11px] text-slate-400">JPG/PNG, en fazla 10MB. Sohbetlerde ve listelerde görünür.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">E-posta (Giriş adresi)</Label>
                <Input value={user?.username || ""} disabled data-testid="profile-email" className="rounded-none border-slate-200 h-11 bg-slate-50 text-slate-500" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Rol</Label>
                <Input value={ROLE_LABELS[user?.role] || user?.role || ""} disabled className="rounded-none border-slate-200 h-11 bg-slate-50 text-slate-500" data-testid="profile-role" />
              </div>
              {!isAdmin && (
                <div className="space-y-1.5">
                  <Label>Firma Adı</Label>
                  <Input value={form.company_name} onChange={set("company_name")} data-testid="profile-company-input" className="rounded-none border-slate-200 h-11" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Yetkili Adı</Label>
                <Input value={form.contact_name} onChange={set("contact_name")} data-testid="profile-name-input" className="rounded-none border-slate-200 h-11" />
              </div>
              <div className="space-y-1.5">
                <Label>Telefon</Label>
                <Input value={form.contact_phone} onChange={set("contact_phone")} placeholder="+90 5xx xxx xx xx" data-testid="profile-phone-input" className="rounded-none border-slate-200 h-11" />
              </div>
              {!isAdmin && (
                <div className="space-y-1.5">
                  <Label>Web Sitesi</Label>
                  <Input value={form.website} onChange={set("website")} placeholder="https://" data-testid="profile-website-input" className="rounded-none border-slate-200 h-11" />
                </div>
              )}
            </div>
            <Button onClick={saveProfile} disabled={saving} data-testid="save-profile-button" className="rounded-none bg-slate-900 text-white hover:bg-slate-700 h-11 px-8 font-semibold transition-colors duration-200">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
            {(user?.last_login || user?.prev_login) && (
              <div className="border-t border-slate-100 pt-4 space-y-1.5" data-testid="login-info">
                {user?.last_login?.at && (
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Clock size={12} className="text-slate-400" />
                    <span>Son giriş: <span className="font-medium text-slate-700" data-testid="last-login">{fmtLogin(user.last_login)}</span></span>
                  </div>
                )}
                {user?.prev_login?.at && (
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Clock size={12} />
                    <span>Önceki giriş: <span data-testid="prev-login">{fmtLogin(user.prev_login)}</span></span>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="bildirimler">
          <div className="border border-slate-200 bg-white p-6 space-y-1" data-testid="notification-prefs">
            <p className="text-sm text-slate-500 mb-4">Hangi otomatik e-postaları almak istediğinizi seçin. Hesap güvenliğiyle ilgili e-postalar (şifre sıfırlama vb.) her zaman gönderilir.</p>
            {(PREFS_BY_ROLE[user?.role] || []).map((p) => (
              <div key={p.key} className="flex items-center justify-between gap-4 py-3 border-b border-slate-100 last:border-b-0">
                <span className="text-sm">{p.label}</span>
                <Switch checked={prefs[p.key] !== false} onCheckedChange={(v) => togglePref(p.key, v)} data-testid={`pref-${p.key}`} />
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="sifre">
          <div className="border border-slate-200 bg-white p-6 space-y-5 max-w-md">
            <div className="space-y-1.5">
              <Label>Mevcut Şifre</Label>
              <PasswordInput value={pw.current} onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))} testid="current-password-input" placeholder="Mevcut şifreniz" />
            </div>
            <div className="space-y-1.5">
              <Label>Yeni Şifre</Label>
              <PasswordInput value={pw.next} onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))} testid="new-password-input" placeholder="En az 6 karakter" />
            </div>
            <div className="space-y-1.5">
              <Label>Yeni Şifre (Tekrar)</Label>
              <PasswordInput value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} testid="confirm-password-input" placeholder="Yeni şifrenizi tekrar girin" />
            </div>
            <Button onClick={changePassword} disabled={pwBusy || !pw.current || !pw.next || !pw.confirm} data-testid="change-password-button" className="rounded-none bg-slate-900 text-white hover:bg-slate-700 h-11 px-8 font-semibold transition-colors duration-200 w-full">
              {pwBusy ? "Değiştiriliyor..." : "Şifreyi Değiştir"}
            </Button>
          </div>
          <div className="border border-slate-200 bg-white p-6 max-w-md mt-6 space-y-3" data-testid="revoke-sessions-card">
            <div className="font-heading font-bold text-base tracking-tight flex items-center gap-2">
              <KeyRound size={15} className="text-slate-600" /> Oturum Güvenliği
            </div>
            <p className="text-xs text-slate-500">
              Hesabınıza ait tüm cihazlardaki oturumları anında geçersiz kılar (ör. anahtar sızıntısı şüphesinde). Bu cihazdaki oturumunuz devam eder.
            </p>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const { data } = await api.post("/me/revoke-tokens");
                  if (data?.token) localStorage.setItem("cop31_token", data.token);
                  toast.success("Diğer tüm cihazlardaki oturumlar sonlandırıldı");
                } catch (e) {
                  toast.error(apiError(e));
                }
              }}
              data-testid="revoke-sessions-button"
              className="rounded-none border-orange-300 text-orange-600 hover:bg-orange-50 h-10 font-semibold transition-colors duration-200 w-full"
            >
              Tüm Oturumları Sonlandır
            </Button>
          </div>
          {fullAdmin && (
          <div className="border border-amber-300 bg-amber-50/40 p-6 space-y-5 max-w-md mt-6" data-testid="super-password-card">
            <div>
              <div className="font-heading font-bold text-lg tracking-tight flex items-center gap-2">
                <KeyRound size={16} className="text-amber-600" /> Süper Şifre
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Hassas işlemlerde (örn. tedarikçi teklif fiyatını düzenleme) ek doğrulama olarak sorulur. Yalnızca süper admin tanımlayıp değiştirebilir.
                {spwSet === false && <span className="block mt-1 text-amber-700 font-medium">Henüz tanımlanmadı.</span>}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>{spwSet ? "Yeni Süper Şifre" : "Süper Şifre"}</Label>
              <PasswordInput value={spw.next} onChange={(e) => setSpw((p) => ({ ...p, next: e.target.value }))} testid="new-super-password-input" placeholder="En az 6 karakter" />
            </div>
            <div className="space-y-1.5">
              <Label>{spwSet ? "Yeni Süper Şifre (Tekrar)" : "Süper Şifre (Tekrar)"}</Label>
              <PasswordInput value={spw.confirm} onChange={(e) => setSpw((p) => ({ ...p, confirm: e.target.value }))} testid="confirm-super-password-input" placeholder="Tekrar girin" />
            </div>
            <Button onClick={changeSuperPassword} disabled={spwBusy || !spw.next || !spw.confirm} data-testid="save-super-password-button" className="rounded-none bg-amber-600 text-white hover:bg-amber-500 h-11 px-8 font-semibold transition-colors duration-200 w-full">
              {spwBusy ? "Kaydediliyor..." : spwSet ? "Süper Şifreyi Değiştir" : "Süper Şifreyi Tanımla"}
            </Button>
            <div className="border-t border-amber-200 pt-4 space-y-3">
              <div className="flex items-center gap-2.5">
                <Checkbox id="ozel-yetki" checked={privileged} onCheckedChange={togglePrivilege} data-testid="ozel-yetki-checkbox" className="border-amber-400 data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600" />
                <Label htmlFor="ozel-yetki" className="font-semibold cursor-pointer">Özel Yetki</Label>
              </div>
              <p className="text-[11px] text-slate-500">
                Etkinleştirildiğinde, bu oturum kapatılana kadar teklif karşılaştırma sayfalarında fiyat düzenleme (kalem) ikonu görünür. Oturum kapandığında otomatik devre dışı kalır.
              </p>
              {askPw && (
                <div className="space-y-2" data-testid="ozel-yetki-verify-area">
                  <PasswordInput value={vpw} onChange={(e) => setVpw(e.target.value)} testid="ozel-yetki-password-input" placeholder="Süper şifrenizi girin" />
                  <div className="flex gap-2">
                    <Button onClick={confirmPrivilege} disabled={vBusy || !vpw} data-testid="ozel-yetki-confirm-button" className="flex-1 rounded-none bg-amber-600 text-white hover:bg-amber-500 h-10 font-semibold transition-colors duration-200">
                      {vBusy ? "Doğrulanıyor..." : "Doğrula ve Etkinleştir"}
                    </Button>
                    <Button variant="outline" onClick={() => { setAskPw(false); setVpw(""); }} data-testid="ozel-yetki-cancel-button" className="rounded-none border-slate-200 hover:bg-slate-100 h-10">
                      Vazgeç
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}
        </TabsContent>
      </Tabs>

      {cropSrc && (
        <AvatarCropDialog
          src={cropSrc}
          busy={avatarBusy}
          onCancel={() => { URL.revokeObjectURL(cropSrc); setCropSrc(null); }}
          onSave={saveCropped}
        />
      )}
    </div>
  );
}
